import type { AgingReport } from '@morales/shared';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { Sheet, type SheetColumn } from '../components/Sheet';
import { byNumber, byText, useSort } from '../components/useSort';
import { Empty, PageHead, QueryView } from '../components/ui';
import { formatDate, formatMoney } from '../format';
import { useDesktop } from '../hooks';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

const BUCKETS = ['current', 'd1to30', 'd31to60', 'd61to90', 'd90plus'] as const;

type Row = AgingReport['rows'][number];
type SortKey = 'customer' | 'terms' | 'limit' | (typeof BUCKETS)[number] | 'unapplied' | 'balance' | 'late';

/** Who owes what, by age (owner/office): the CPA's aging, with the credit limit and the hold next to it. */
export function AgingPage() {
  const desktop = useDesktop();
  return desktop ? <AgingSheet /> : <AgingList />;
}

/** QuickBooks' A/R aging summary on the sheet: a row per customer, a column per age, unapplied money, totals. */
function AgingSheet() {
  const { t, intlLocale } = useLocale();
  const navigate = useNavigate();
  const query = useQuery({ queryKey: qk.aging, queryFn: ({ signal }) => api.aging(signal) });
  const sorters = useMemo(
    () => ({
      customer: byText<Row>((r) => r.customerName),
      terms: byNumber<Row>((r) => r.termsDays),
      limit: byNumber<Row>((r) => r.creditLimit),
      current: byNumber<Row>((r) => r.buckets.current),
      d1to30: byNumber<Row>((r) => r.buckets.d1to30),
      d31to60: byNumber<Row>((r) => r.buckets.d31to60),
      d61to90: byNumber<Row>((r) => r.buckets.d61to90),
      d90plus: byNumber<Row>((r) => r.buckets.d90plus),
      unapplied: byNumber<Row>((r) => r.unapplied),
      balance: byNumber<Row>((r) => r.netBalance),
      late: byNumber<Row>((r) => r.oldestDaysLate),
    }),
    [],
  );
  const { sorted, head } = useSort<Row, SortKey>(sorters, { key: 'balance', dir: -1 }, ['limit', ...BUCKETS, 'unapplied', 'balance', 'late']);
  const money = (v: number) => (v === 0 ? '' : formatMoney(v, intlLocale));

  return (
    <div className="desk-page">
      <header className="desk-head">
        <div>
          <h1>{t('agingTitle')}</h1>
          <p className="muted">{t('agingHint')}</p>
        </div>
      </header>
      <QueryView query={query}>
        {(r) => {
          const rows = sorted(r.rows);
          // after unapplied money, the same number as the dashboard (decision 73); the age columns stay gross like QuickBooks'
          const pastDue = r.totals.pastDue;
          const columns: SheetColumn<Row>[] = [
            {
              key: 'customer',
              header: head('customer', t('customer')),
              label: t('customer'),
              width: '168px',
              render: (x) => <Link to={`/customers/${x.customerId}/statement`}>{x.customerName}</Link>,
            },
            { key: 'terms', header: head('terms', t('terms')), label: t('terms'), width: '64px', render: (x) => t('netDays').replace('{n}', String(x.termsDays)) },
            { key: 'limit', header: head('limit', t('creditLimit')), label: t('creditLimit'), width: '92px', align: 'right', render: (x) => (x.creditLimit === null ? t('noLimit') : formatMoney(x.creditLimit, intlLocale)), cellClass: (x) => (x.creditLimit !== null && x.netBalance > x.creditLimit ? 'bad' : undefined) },
            ...BUCKETS.map(
              (b): SheetColumn<Row> => ({
                key: b,
                header: head(b, t(`bucket_${b}`)),
                label: t(`bucket_${b}`),
                width: '84px',
                align: 'right',
                render: (x) => money(x.buckets[b]),
                cellClass: (x) => (x.buckets[b] > 0 && (b === 'd61to90' || b === 'd90plus') ? 'bad' : x.buckets[b] > 0 && b === 'd31to60' ? 'warn' : undefined),
              }),
            ),
            { key: 'unapplied', header: head('unapplied', t('unappliedCol')), label: t('unappliedCol'), hint: t('unappliedHint'), width: '88px', align: 'right', render: (x) => (x.unapplied ? `−${formatMoney(x.unapplied, intlLocale)}` : '') },
            { key: 'balance', header: head('balance', t('balance')), label: t('balance'), width: '100px', align: 'right', render: (x) => <strong>{formatMoney(x.netBalance, intlLocale)}</strong> },
            { key: 'late', header: head('late', t('daysLateCol')), label: t('daysLateCol'), width: '64px', align: 'right', render: (x) => (x.oldestDaysLate > 0 ? String(x.oldestDaysLate) : '') },
            {
              key: 'collection',
              header: t('collectionCol'),
              width: '120px',
              render: (x) => (
                <span>
                  {x.onHold ? <span className="state state-overdue">{t('onHold')}</span> : null}{' '}
                  {x.noticeDue ? <span className="state state-overdue">{t('letterDue')}</span> : x.lastNoticeOn ? `${t('letterSent')} ${formatDate(x.lastNoticeOn, intlLocale, 'date')}` : ''}
                </span>
              ),
            },
          ];
          const footer = (
            <tr>
              <td>{t('reportTotal')}</td>
              <td />
              <td />
              {BUCKETS.map((b) => (
                <td key={b} className="al-right" data-testid={`aging-total-${b}`}>
                  {formatMoney(r.totals[b], intlLocale)}
                </td>
              ))}
              <td className="al-right">{r.totals.unapplied ? `−${formatMoney(r.totals.unapplied, intlLocale)}` : ''}</td>
              <td className="al-right" data-testid="aging-total">
                <strong>{formatMoney(r.totals.netBalance, intlLocale)}</strong>
              </td>
              <td />
              <td />
            </tr>
          );
          return (
            <>
              {!r.ledgerStarted ? <p className="note">{t('balancePending')}</p> : null}
              <dl className="totals-strip" data-testid="aging-strip">
                <div>
                  <dt>{t('customersCol')}</dt>
                  <dd>{r.rows.length}</dd>
                </div>
                <div>
                  <dt>{t('balance')}</dt>
                  <dd>{formatMoney(r.totals.netBalance, intlLocale)}</dd>
                </div>
                <div>
                  <dt>{t('bucket_current')}</dt>
                  <dd>{formatMoney(r.totals.current, intlLocale)}</dd>
                </div>
                <div className={pastDue > 0 ? 'bad' : undefined}>
                  <dt>{t('overdueTotal')}</dt>
                  <dd>{formatMoney(pastDue, intlLocale)}</dd>
                </div>
                <div className={r.totals.d90plus > 0 ? 'bad' : undefined}>
                  <dt>{t('bucket_d90plus')}</dt>
                  <dd>{formatMoney(r.totals.d90plus, intlLocale)}</dd>
                </div>
                <div>
                  <dt>{t('unappliedCol')}</dt>
                  <dd>{formatMoney(r.totals.unapplied, intlLocale)}</dd>
                </div>
              </dl>
              {rows.length === 0 ? (
                <p className="muted pick-first">{t('noResults')}</p>
              ) : (
                <Sheet label={t('agingTitle')} columns={columns} rows={rows} rowKey={(x) => x.customerId} rowLabel={(x) => x.customerName} stickyFirst minWidth="1116px" footer={footer} onOpen={(x) => navigate(`/customers/${x.customerId}/statement`)} />
              )}
            </>
          );
        }}
      </QueryView>
    </div>
  );
}

/** The phone's aging: the total card, then a row per customer. */
function AgingList() {
  const { t, intlLocale } = useLocale();
  const query = useQuery({ queryKey: qk.aging, queryFn: ({ signal }) => api.aging(signal) });
  return (
    <div className="stack">
      <PageHead title={t('agingTitle')} back="/" />
      <p className="muted">{t('agingHint')}</p>
      <QueryView query={query}>
        {(r) => (
          <>
            {!r.ledgerStarted ? <p className="note">{t('balancePending')}</p> : null}
            <section className="card">
              <h2>{t('total')}</h2>
              <dl className="facts plain">
                <div className="fact">
                  <dt>{t('balance')}</dt>
                  <dd className="money owed" data-testid="aging-total">
                    {formatMoney(r.totals.netBalance, intlLocale)}
                  </dd>
                </div>
                {r.totals.unapplied ? (
                  <div className="fact">
                    <dt>{t('unappliedCol')}</dt>
                    <dd>−{formatMoney(r.totals.unapplied, intlLocale)}</dd>
                  </div>
                ) : null}
                {BUCKETS.map((b) => (
                  <div key={b} className="fact">
                    <dt>{t(`bucket_${b}`)}</dt>
                    <dd>{formatMoney(r.totals[b], intlLocale)}</dd>
                  </div>
                ))}
              </dl>
            </section>
            {r.rows.length === 0 ? (
              <Empty>{t('noResults')}</Empty>
            ) : (
              <ul className="list" data-testid="aging-rows">
                {r.rows.map((row) => (
                  <li key={row.customerId}>
                    <Link to={`/customers/${row.customerId}/statement`} className="row">
                      <span className="row-main">
                        <span className="row-title">
                          {row.customerName}
                          {row.onHold ? <span className="badge badge-danger">{t('onHold')}</span> : null}
                          {row.noticeDue ? <span className="badge badge-warn">{t('letterDue')}</span> : row.lastNoticeOn ? <span className="badge">{t('letterSent')} {formatDate(row.lastNoticeOn, intlLocale, 'date')}</span> : null}
                        </span>
                        <span className="row-sub">
                          {[...BUCKETS.filter((b) => row.buckets[b] > 0).map((b) => `${t(`bucket_${b}`)} ${formatMoney(row.buckets[b], intlLocale)}`), ...(row.unapplied ? [`${t('unappliedCol')} −${formatMoney(row.unapplied, intlLocale)}`] : [])].join(' · ')}
                        </span>
                        <span className="row-sub">
                          {t('creditLimit')}: {row.creditLimit === null ? t('noLimit') : formatMoney(row.creditLimit, intlLocale)} · {t('oldest')} {row.oldestDaysLate} {t('daysLateShort')}
                        </span>
                      </span>
                      <span className="row-end">
                        <span className={row.buckets.d90plus > 0 || row.buckets.d61to90 > 0 ? 'row-end-value owed' : 'row-end-value'}>{formatMoney(row.netBalance, intlLocale)}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </QueryView>
    </div>
  );
}
